import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/context/CurrencyContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Wallet as WalletIcon, 
  Plus, 
  ArrowUpRight, 
  ArrowDownLeft,
  CreditCard,
  Clock,
  CheckCircle2,
  Loader2,
  Copy,
  Building2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WalletPage = () => {
  const { user } = useAuth();
  const { currency, formatPrice, currencies } = useCurrency();
  const [searchParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [gateways, setGateways] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [topupDialogOpen, setTopupDialogOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState('');
  const [selectedGateway, setSelectedGateway] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState(currency);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchWalletData();
    fetchGateways();
    
    // Check for topup callback
    const topupStatus = searchParams.get('topup');
    if (topupStatus === 'success') {
      toast.success('Wallet top-up successful!');
      fetchWalletData();
    } else if (topupStatus === 'cancelled') {
      toast.info('Top-up was cancelled');
    }
  }, []);

  const fetchWalletData = async () => {
    try {
      const [walletRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/wallet`),
        axios.get(`${API}/wallet/transactions`)
      ]);
      setWallet(walletRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching wallet:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGateways = async () => {
    try {
      const response = await axios.get(`${API}/payment-gateways`);
      const enabledGateways = response.data.filter(g => g.enabled);
      setGateways(enabledGateways);
      
      // Find bank_transfer gateway to get bank details
      const bankTransfer = response.data.find(g => g.id === 'bank_transfer');
      if (bankTransfer && bankTransfer.bank_details) {
        setBankDetails(bankTransfer.bank_details);
      }
    } catch (error) {
      console.error('Error fetching gateways:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const handleTopup = async () => {
    const amount = parseFloat(topupAmount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    if (amount < 100) {
      toast.error('Minimum top-up amount is ₦100');
      return;
    }

    setProcessing(true);
    try {
      const response = await axios.post(`${API}/wallet/topup`, {
        amount,
        currency: 'NGN', // Always NGN
        payment_gateway: selectedGateway
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.bank_details) {
        // Bank transfer - show details
        toast.success('Please transfer to the bank account shown');
        setTopupDialogOpen(false);
      } else {
        toast.success('Top-up initiated successfully');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate top-up');
      setProcessing(false);
    }
  };

  const getGatewayLabel = (gateway) => {
    const labels = {
      stripe: { name: 'Stripe', icon: '💳', description: 'International cards' },
      paystack: { name: 'Paystack', icon: '🏦', description: 'Nigerian cards & bank' },
      monnify: { name: 'Monnify', icon: '🇳🇬', description: 'Nigerian bank transfer' },
      bank_transfer: { name: 'Bank Transfer', icon: '🏛️', description: 'Direct bank transfer' }
    };
    return labels[gateway] || { name: gateway, icon: '💰', description: '' };
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="wallet-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">My Wallet</h1>
            <p className="text-muted-foreground mt-1">Manage your wallet balance and transactions</p>
          </div>
          <Button 
            className="bg-sky-600 hover:bg-sky-700"
            onClick={() => setTopupDialogOpen(true)}
            data-testid="topup-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Top Up Wallet
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {/* Balance Card */}
            <div className="card-clinical bg-gradient-to-br from-sky-600 to-sky-700 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sky-100 text-sm">Available Balance</p>
                  <p className="text-4xl font-bold mt-2" data-testid="wallet-balance">
                    {formatPrice(wallet?.balance || 0, wallet?.currency || 'USD')}
                  </p>
                  <p className="text-sky-200 text-sm mt-2">
                    Currency: {wallet?.currency || 'USD'}
                  </p>
                </div>
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                  <WalletIcon className="w-8 h-8" />
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <button 
                className="card-clinical hover:shadow-md transition-shadow text-left"
                onClick={() => setTopupDialogOpen(true)}
              >
                <div className="w-10 h-10 bg-emerald-50 dark:bg-emerald-900/30 rounded-lg flex items-center justify-center mb-3">
                  <Plus className="w-5 h-5 text-emerald-600" />
                </div>
                <p className="font-medium text-foreground">Add Funds</p>
                <p className="text-sm text-muted-foreground">Top up wallet</p>
              </button>
              <div className="card-clinical">
                <div className="w-10 h-10 bg-sky-50 dark:bg-sky-900/30 rounded-lg flex items-center justify-center mb-3">
                  <ArrowUpRight className="w-5 h-5 text-sky-600" />
                </div>
                <p className="font-medium text-foreground">{transactions.filter(t => t.transaction_type === 'credit').length}</p>
                <p className="text-sm text-muted-foreground">Credits</p>
              </div>
              <div className="card-clinical">
                <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/30 rounded-lg flex items-center justify-center mb-3">
                  <ArrowDownLeft className="w-5 h-5 text-amber-600" />
                </div>
                <p className="font-medium text-foreground">{transactions.filter(t => t.transaction_type === 'debit').length}</p>
                <p className="text-sm text-muted-foreground">Debits</p>
              </div>
              <div className="card-clinical">
                <div className="w-10 h-10 bg-purple-50 dark:bg-purple-900/30 rounded-lg flex items-center justify-center mb-3">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                </div>
                <p className="font-medium text-foreground">{transactions.length}</p>
                <p className="text-sm text-muted-foreground">Transactions</p>
              </div>
            </div>

            {/* Transaction History */}
            <div className="card-clinical">
              <h2 className="text-lg font-semibold text-foreground mb-6">Transaction History</h2>
              {transactions.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">No transactions yet</p>
                  <p className="text-sm text-muted-foreground mt-1">Your transaction history will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div 
                      key={tx.id} 
                      className="flex items-center justify-between p-4 bg-muted/50 rounded-lg"
                      data-testid={`transaction-${tx.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          tx.transaction_type === 'credit' 
                            ? 'bg-emerald-100 dark:bg-emerald-900/30' 
                            : 'bg-red-100 dark:bg-red-900/30'
                        }`}>
                          {tx.transaction_type === 'credit' ? (
                            <ArrowDownLeft className="w-5 h-5 text-emerald-600" />
                          ) : (
                            <ArrowUpRight className="w-5 h-5 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-foreground">{tx.description}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(tx.created_at).toLocaleDateString()} • {tx.reference.slice(0, 12)}...
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${
                          tx.transaction_type === 'credit' ? 'text-emerald-600' : 'text-red-600'
                        }`}>
                          {tx.transaction_type === 'credit' ? '+' : '-'}
                          {formatPrice(tx.amount, tx.currency)}
                        </p>
                        <Badge variant="outline" className={
                          tx.status === 'completed' 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-100' 
                            : 'bg-amber-50 text-amber-700 border-amber-100'
                        }>
                          {tx.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Top-up Dialog */}
        <Dialog open={topupDialogOpen} onOpenChange={setTopupDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Top Up Wallet</DialogTitle>
              <DialogDescription>
                Add funds to your wallet using your preferred payment method
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label>Amount (₦)</Label>
                <Input
                  type="number"
                  placeholder="Enter amount (min ₦100)"
                  value={topupAmount}
                  onChange={(e) => setTopupAmount(e.target.value)}
                  className="mt-1"
                  min="100"
                  data-testid="topup-amount-input"
                />
              </div>
              <div>
                <Label>Payment Method</Label>
                <div className="mt-2 space-y-2">
                  {gateways.length > 0 ? gateways.map((g) => {
                    const label = getGatewayLabel(g.id);
                    return (
                      <button
                        key={g.id}
                        onClick={() => setSelectedGateway(g.id)}
                        className={`w-full p-3 rounded-lg border text-left transition-colors ${
                          selectedGateway === g.id 
                            ? 'border-sky-500 bg-sky-50 dark:bg-sky-900/30' 
                            : 'border-border hover:border-sky-300'
                        }`}
                        data-testid={`gateway-${g.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xl">{label.icon}</span>
                            <div>
                              <p className="font-medium text-foreground">{label.name}</p>
                              <p className="text-xs text-muted-foreground">{label.description}</p>
                            </div>
                          </div>
                          {selectedGateway === g.id && (
                            <CheckCircle2 className="w-5 h-5 text-sky-600" />
                          )}
                        </div>
                      </button>
                    );
                  }) : (
                    <div className="text-center py-4 text-muted-foreground">
                      Loading payment methods...
                    </div>
                  )}
                </div>
              </div>

              {/* Bank Transfer Details */}
              {selectedGateway === 'bank_transfer' && bankDetails && (
                <div className="p-4 bg-sky-50 dark:bg-sky-900/20 rounded-lg border border-sky-200 dark:border-sky-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="w-5 h-5 text-sky-600" />
                    <h3 className="font-medium text-foreground">Bank Account Details</h3>
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Bank Name:</span>
                      <span className="font-medium text-foreground">{bankDetails.bank_name}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Account Number:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-foreground">{bankDetails.account_number}</span>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0"
                          onClick={() => copyToClipboard(bankDetails.account_number)}
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">Account Name:</span>
                      <span className="font-medium text-foreground">{bankDetails.account_name}</span>
                    </div>
                  </div>
                  <p className="text-xs text-sky-700 dark:text-sky-400 mt-3">
                    Transfer the amount above and contact support for confirmation.
                  </p>
                </div>
              )}

              <Button 
                className="w-full bg-sky-600 hover:bg-sky-700"
                onClick={handleTopup}
                disabled={processing || !selectedGateway}
                data-testid="confirm-topup-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CreditCard className="w-4 h-4 mr-2" />
                    Proceed to Payment
                  </>
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default WalletPage;
